import { useEffect, useState } from "react";

type SaqueItem = {
  id: number;
  data_solicitacao?: string;
  anfitriao_nome?: string;
  anfitriao_email?: string;
  anfitriao_telefone?: string;
  valor?: number | string;
  valor_cents?: number | string;
  pix_tipo?: string;
  pix_chave?: string;
  status?: string;
  observacao_admin?: string | null;
};

type ResumoFinanceiro = {
  total_pendente?: number | string;
  total_pago?: number | string;
  total_solicitado?: number | string;
  quantidade_pendentes?: number;
};

type FinanceiroResponse = {
  resumo?: ResumoFinanceiro;
  saques?: SaqueItem[];
};

function formatarMoeda(valor: number) {
  return valor.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}


function formatarData(data?: string) {
  if (!data) return "-";
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return data;
  return d.toLocaleDateString("pt-BR");
}

function corStatus(status?: string) {
  switch (status) {
    case "solicitado":
      return "bg-yellow-100 text-yellow-800";
    case "em_analise":
      return "bg-blue-100 text-blue-800";
    case "pago":
      return "bg-green-100 text-green-800";
    case "recusado":
      return "bg-red-100 text-red-800";
    case "cancelado":
      return "bg-slate-200 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

export default function AdminFinanceiro() {
  const [dados, setDados] = useState<FinanceiroResponse>({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [processandoId, setProcessandoId] = useState<number | null>(null);

  async function carregarDados() {
    try {
      setLoading(true);
      setErro("");

      const response = await fetch("/admin/financeiro/dados", {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();
      setDados(json);
    } catch (error) {
      console.error("Erro ao carregar financeiro admin:", error);
      setErro("Não foi possível carregar o financeiro do admin.");
    } finally {
      setLoading(false);
    }
  }

  async function acaoSaque(
    id: number,
    acao: "aprovar" | "rejeitar" | "marcar-pago"
  ) {
    try {
      setProcessandoId(id);

      const token =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || "";

      const response = await fetch(`/admin/financeiro/saques/${id}/${acao}`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": token,
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json?.message || "Erro ao executar ação.");
      }

      alert(json?.message || "Ação realizada com sucesso.");
      await carregarDados();
    } catch (error: any) {
      console.error("Erro ao executar ação do saque:", error);
      alert(error?.message || "Erro ao executar ação do saque.");
    } finally {
      setProcessandoId(null);
    }
  }

  useEffect(() => {
    carregarDados();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Controle Financeiro
          </h1>
          <p className="text-sm text-slate-600">
            Acompanhe pedidos de saque, status e dados Pix registrados no
            momento da solicitação.
          </p>
        </div>

        {loading && (
          <div className="rounded-2xl border bg-white p-6 text-sm text-slate-600">
            Carregando dados financeiros...
          </div>
        )}

        {!!erro && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {erro}
          </div>
        )}

        {!loading && !erro && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="text-sm text-slate-500">Total pendente</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">
                  {formatarMoeda(Number(dados.resumo?.total_pendente || 0))}
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="text-sm text-slate-500">Total pago</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">
                  {formatarMoeda(Number(dados.resumo?.total_pago || 0))}
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="text-sm text-slate-500">Total solicitado</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">
                  {formatarMoeda(Number(dados.resumo?.total_solicitado || 0))}
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="text-sm text-slate-500">Pendentes</div>
                <div className="mt-2 text-2xl font-bold text-slate-900">
                  {dados.resumo?.quantidade_pendentes ?? 0}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
              <div className="border-b px-4 py-3">
                <h2 className="text-lg font-semibold text-slate-900">
                  Pedidos de saque
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100 text-left text-slate-700">
                    <tr>
                      <th className="px-4 py-3">ID</th>
                      <th className="px-4 py-3">Data</th>
                      <th className="px-4 py-3">Anfitrião</th>
                      <th className="px-4 py-3">Valor</th>
                      <th className="px-4 py-3">Pix</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(dados.saques || []).length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                          Nenhum pedido de saque encontrado.
                        </td>
                      </tr>
                    ) : (
                      (dados.saques || []).map((saque) => (
                        <tr key={saque.id} className="border-t align-top">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {saque.id}
                          </td>

                          <td className="px-4 py-3 text-slate-700">
                            {formatarData(saque.data_solicitacao)}
                          </td>

                          <td className="px-4 py-3 text-slate-700">
                            <div className="font-medium">
                              {saque.anfitriao_nome || "-"}
                            </div>
                            <div className="text-xs text-slate-500">
                              {saque.anfitriao_email || "-"}
                            </div>
                            {saque.anfitriao_telefone && (
                              <div className="text-xs text-slate-500">
                                {saque.anfitriao_telefone}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3 text-slate-700">
                            {formatarMoeda(Number(saque.valor || 0))}
                          </td>

                          <td className="px-4 py-3 text-slate-700">
                            <div>{saque.pix_tipo || "-"}</div>
                            <div className="break-all text-xs text-slate-500">
                              {saque.pix_chave || "-"}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${corStatus(
                                saque.status
                              )}`}
                            >
                              {saque.status || "-"}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              {saque.status === "solicitado" && (
                                <>
                                  <button
                                    type="button"
                                    disabled={processandoId === saque.id}
                                    onClick={() => acaoSaque(saque.id, "aprovar")}
                                    className="rounded-md bg-blue-600 px-3 py-1 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Aprovar
                                  </button>

                                  <button
                                    type="button"
                                    disabled={processandoId === saque.id}
                                    onClick={() => acaoSaque(saque.id, "rejeitar")}
                                    className="rounded-md bg-red-600 px-3 py-1 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Rejeitar
                                  </button>
                                </>
                              )}

                              {saque.status === "em_analise" && (
                                <>
                                  <button
                                    type="button"
                                    disabled={processandoId === saque.id}
                                    onClick={() =>
                                      acaoSaque(saque.id, "marcar-pago")
                                    }
                                    className="rounded-md bg-green-600 px-3 py-1 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Marcar como pago
                                  </button>

                                  <button
                                    type="button"
                                    disabled={processandoId === saque.id}
                                    onClick={() => acaoSaque(saque.id, "rejeitar")}
                                    className="rounded-md bg-red-600 px-3 py-1 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Rejeitar
                                  </button>
                                </>
                              )}

                              {processandoId === saque.id && (
                                <span className="text-xs text-slate-500">
                                  Processando...
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}